'use client';

import { memo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Badge } from '@/components/ui/badge';
import { nameToGradient } from '@/lib/avatar-gradient';
import { hasProductPrice } from '@sweetspot/shared';
import { formatCategory, formatCurrency } from '@/lib/utils';
import type { ProductFeedItem } from '@/lib/api';
import { InstagramProductPhoto } from '@/components/instagram/instagram-product-photo';
import { Package } from 'lucide-react';

interface ProductCardProps {
  product: ProductFeedItem;
}

/**
 * Marketplace product tile. Price leads, then the title, then one grey meta
 * line (store · unit) — the scannable card anatomy from
 * docs/mobile-ux-fb-marketplace-adoption.md §1.6, sized to sit two-up on a
 * phone.
 */
export const ProductCard = memo(function ProductCard({ product }: ProductCardProps) {
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  const seller = product.sellerBusiness;
  const href = seller?.slug ? `/s/${seller.slug}/p/${product.id}` : '#';
  const price = Number(product.price ?? 0);

  return (
    <div
      className="group relative block overflow-hidden rounded-xl border bg-card transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md active:scale-[0.98] active:duration-100"
    >
      <div className="relative aspect-square overflow-hidden bg-muted">
        <InstagramProductPhoto photo={product.instagramPhoto} title={product.title} imageHref={href}
          className="h-full w-full" imageClassName="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105">
          {product.imageUrl && !imgError ? (
            <Image
              src={product.imageUrl}
              alt={product.title}
              fill
              sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
              className={`img-fade object-cover transition-transform duration-300 group-hover:scale-105 ${imgLoaded ? 'is-loaded' : ''}`}
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgError(true)}
            />
          ) : (
            <div
              className={`flex h-full w-full items-center justify-center bg-gradient-to-br ${nameToGradient(product.title)}`}
            >
              <Package className="h-7 w-7 text-white/80" />
            </div>
          )}

        </InstagramProductPhoto>
        <div className="pointer-events-none absolute bottom-2 left-2 right-2 z-10 flex flex-wrap items-end justify-between gap-1">
          {product.category && (
            <div>
              <Badge className="border-0 bg-primary/90 text-[9px] text-primary-foreground backdrop-blur-sm sm:text-[10px]">
                {formatCategory(product.category)}
              </Badge>
            </div>
          )}

          {product.minQty > 1 && (
            <div className="ml-auto">
              <span className="rounded-md bg-background/90 px-1.5 py-0.5 text-[10px] font-medium backdrop-blur-sm">
                Min {product.minQty}
              </span>
            </div>
          )}
        </div>
      </div>

      <Link href={href} prefetch={false} className="block space-y-1 p-2.5 sm:p-3">
        <p className="text-sm font-semibold leading-tight sm:text-base">
          {hasProductPrice(price) ? formatCurrency(price) : 'Coming soon'}
          {hasProductPrice(price) && <span className="text-[11px] font-normal text-muted-foreground"> / {product.unit}</span>}
        </p>
        <h3 className="line-clamp-2 text-[13px] leading-tight text-foreground sm:text-sm">
          {product.title}
        </h3>
        <p className="truncate text-[11px] text-muted-foreground">
          {seller?.name ?? 'Store'}
          {product.leadTimeDays > 0 && ` · ${product.leadTimeDays}d lead`}
        </p>
      </Link>
    </div>
  );
});
